/**
 * useConfirmedAuth — the ONE authoritative "is a real user signed in" signal.
 *
 * 2026-08-15 owner incident (second occurrence): portal pages rendered for a
 * signed-out visitor because route guards accepted the localStorage-cached
 * AuthContext identity — a cache backed by a client-minted random token that
 * nothing could ever expire — OR'd with a one-shot Firebase probe that never
 * re-evaluated after mount. This hook is the shared replacement:
 *
 *   • Firebase's onAuthStateChanged is the ONLY authority — no localStorage.
 *   • The subscription stays LIVE, so a mid-session sign-out or account
 *     switch re-evaluates every consumer (pattern proven in
 *     JusticeAccessGate; extracted here for reuse).
 *   • Anonymous users never count as signed in.
 *   • Firebase init/observer failure fails CLOSED (signed out).
 *   • `checked` is the hydration gate — render a neutral loading beat until
 *     it flips true, never a guess.
 *   • An answer Firebase already gave this visit is not asked for again
 *     (D-92 part 4): a guard mounting after it starts checked (below).
 */
import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, type Auth, type User as FirebaseUser } from 'firebase/auth';

export interface ConfirmedAuthState {
  /** First Firebase resolution has landed — until then show a loading beat. */
  checked: boolean;
  /** A real (non-anonymous) Firebase user is signed in RIGHT NOW. */
  signedIn: boolean;
  /** The confirmed user's uid, else null. */
  uid: string | null;
  /** The confirmed user's email, else null. */
  email: string | null;
}

const SIGNED_OUT: Omit<ConfirmedAuthState, 'checked'> = { signedIn: false, uid: null, email: null };
const UNCHECKED: ConfirmedAuthState = { checked: false, ...SIGNED_OUT };

/** The answer for a Firebase user (or none): anonymous users never count as signed in. */
function answerFor(u: FirebaseUser | null): ConfirmedAuthState {
  const real = !!u && !u.isAnonymous;
  return {
    checked: true,
    signedIn: real,
    uid: real && u ? u.uid : null,
    email: real && u ? (u.email || null) : null,
  };
}

const sameAnswer = (a: ConfirmedAuthState, b: ConfirmedAuthState): boolean =>
  a.checked === b.checked && a.signedIn === b.signedIn && a.uid === b.uid && a.email === b.email;

/**
 * THE ANSWER ALREADY GIVEN (D-92 part 4, Miles 2026-10-24: "no matter what the process/transition").
 *
 * Every guard that mounted asked Firebase again and showed its loading screen until the answer came back, a frame or
 * more, though Firebase had answered this visit already: on the way into the app from a public page, and wherever a
 * guard mounts anew. Once this module's listener has heard an auth instance answer, that instance's `currentUser` is
 * the settled answer (Firebase updates it before it tells its listeners, on every sign-in and sign-out), so a new mount
 * starts from it, checked, and still listens live. Only an answer heard here counts: never storage, never another auth
 * instance, never an observer's error; anything that throws starts unchecked, as before.
 */
let answeredBy: Auth | null = null;

/** Where a new mount starts: the settled answer when this auth instance has answered, else unchecked. */
function startingAnswer(): ConfirmedAuthState {
  try {
    const auth = getAuth();
    return answeredBy !== null && auth === answeredBy ? answerFor(auth.currentUser) : UNCHECKED;
  } catch {
    return UNCHECKED;
  }
}

/** Tests only: forget that Firebase has answered. */
export function resetConfirmedAuthForTests(): void {
  answeredBy = null;
}

export function useConfirmedAuth(): ConfirmedAuthState {
  const [state, setState] = useState<ConfirmedAuthState>(startingAnswer);

  useEffect(() => {
    try {
      const auth = getAuth();
      const unsub = onAuthStateChanged(
        auth,
        (u: FirebaseUser | null) => {
          answeredBy = auth;
          const next = answerFor(u);
          setState((prev) => (sameAnswer(prev, next) ? prev : next));
        },
        () => {
          // An observer that failed vouches for nothing: the next mount asks again.
          answeredBy = null;
          setState({ checked: true, ...SIGNED_OUT });
        },
      );
      return () => { try { unsub(); } catch { /* ignore */ } };
    } catch {
      setState({ checked: true, ...SIGNED_OUT });
      return undefined;
    }
  }, []);

  return state;
}

export default useConfirmedAuth;
